import type {
  CreateCredentialPayload,
  Credential,
  CredentialType,
  UpdateCredentialPayload,
} from "@/features/credentials/models/credential";
import { CREDENTIAL_TYPES } from "@/features/credentials/models/credential";
import type { TFunction } from "i18next";
import { z } from "zod";

export interface CreateCredentialFormValues {
  credentialType: CredentialType;
  name: string;
  username: string;
  password: string;
  expiryDate: string;
  competitions: string[];
  seasons: string[];
  ids: string[];
}

export const createCredentialDefaultValues: CreateCredentialFormValues = {
  credentialType: "ENTITY",
  name: "",
  username: "",
  password: "",
  expiryDate: "",
  competitions: [],
  seasons: [],
  ids: [],
};

function getCredentialFieldsSchema(t: TFunction, { passwordRequired }: { passwordRequired: boolean }) {
  return z.object({
    credentialType: z.enum(CREDENTIAL_TYPES),
    name: z.string().trim().min(1, t("credentials.create.errors.name_required")),
    username: z.string().trim().min(1, t("credentials.create.errors.username_required")),
    password: passwordRequired
      ? z.string().min(1, t("credentials.create.errors.password_required"))
      : z.string(),
    expiryDate: z.string().min(1, t("credentials.create.errors.expiry_date_required")),
    competitions: z.array(z.string()),
    seasons: z.array(z.string()),
    ids: z.array(z.string()),
  });
}

function refineCredentialScope(t: TFunction) {
  return (values: CreateCredentialFormValues, ctx: z.RefinementCtx) => {
    if (values.credentialType === "ALL") {
      return;
    }

    if (values.competitions.length === 0) {
      ctx.addIssue({
        code: "custom",
        path: ["competitions"],
        message: t("credentials.create.errors.competitions_required"),
      });
    }

    if (values.ids.length === 0) {
      ctx.addIssue({
        code: "custom",
        path: ["ids"],
        message:
          values.credentialType === "VENUE"
            ? t("credentials.create.errors.venues_required")
            : t("credentials.create.errors.teams_required"),
      });
    }
  };
}

export function getCreateCredentialSchema(t: TFunction) {
  return getCredentialFieldsSchema(t, { passwordRequired: true }).superRefine(refineCredentialScope(t));
}

export function getUpdateCredentialSchema(t: TFunction) {
  return getCredentialFieldsSchema(t, { passwordRequired: false }).superRefine(refineCredentialScope(t));
}

export function getTodayIsoDate() {
  return new Date().toISOString().split("T")[0];
}

export function toDateInputValue(value: string) {
  return value.split("T")[0] ?? "";
}

export function getFormValuesFromCredential(credential: Credential): CreateCredentialFormValues {
  return {
    credentialType: credential.credentialType,
    name: credential.name,
    username: credential.username,
    password: "",
    expiryDate: toDateInputValue(credential.expiryDate),
    competitions: (credential.competitions ?? []).map(String),
    seasons: [],
    ids: (credential.ids ?? []).map(String),
  };
}

export function toCreateCredentialPayload(values: CreateCredentialFormValues): CreateCredentialPayload {
  const payload: CreateCredentialPayload = {
    name: values.name.trim(),
    username: values.username.trim(),
    password: values.password,
    credentialType: values.credentialType,
    startDate: getTodayIsoDate(),
    expiryDate: values.expiryDate,
  };

  if (values.credentialType !== "ALL") {
    payload.competitions = values.competitions;
    payload.ids = values.ids;
  }

  return payload;
}

export function toUpdateCredentialPayload(
  values: CreateCredentialFormValues,
  credential: Credential,
): UpdateCredentialPayload {
  const payload: UpdateCredentialPayload = {
    name: values.name.trim(),
    startDate: toDateInputValue(credential.startDate),
    expiryDate: values.expiryDate,
    competitions: values.credentialType === "ALL" ? [] : values.competitions,
    ids: values.credentialType === "ALL" ? [] : values.ids,
    permissions: credential.permissions ?? {},
  };

  const password = values.password.trim();
  if (password) {
    payload.password = password;
  }

  return payload;
}
